import React, { useState, useEffect, useRef } from 'react';

const PRIORITIES = [
  { value: 'low',    label: 'Low',    dot: '#22c55e', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  { value: 'medium', label: 'Medium', dot: '#f59e0b', bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  { value: 'high',   label: 'High',   dot: '#ef4444', bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
];

const CreateNewTestCaseModal = ({ onClose, onSave, testCase, setTestCase }) => {
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    nameRef.current?.focus();
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const validate = () => {
    const errs = {};
    if (!testCase.name.trim()) errs.name = 'Test case name is required';
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave?.();
    close();
  };

  const selectedPriority = PRIORITIES.find(p => p.value === testCase.priority);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: visible
            ? 'translate(-50%, -50%) scale(1)'
            : 'translate(-50%, -48%) scale(0.97)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s ease',
          zIndex: 1001,
          width: '100%',
          maxWidth: '440px',
          padding: '0 1rem',
          boxSizing: 'border-box',
        }}
      >
        <div style={{
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08)',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
        }}>

          {/* Header */}
          <div style={{
            padding: '1.25rem 1.5rem 1rem',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '8px',
                background: 'linear-gradient(135deg, #eff6ff, #f5f3ff)',
                border: '1px solid #ddd6fe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem',
              }}>🧪</div>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  New Test Case
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '1px' }}>
                  Fill in the details below
                </div>
              </div>
            </div>

            <button
              onClick={close}
              style={{
                width: 28, height: 28, borderRadius: '7px',
                border: '1px solid #e2e8f0',
                background: 'white',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8',
                fontSize: '1rem',
                lineHeight: 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#94a3b8'; }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Test Case Name */}
            <div>
              <label style={{
                display: 'block', fontSize: '0.75rem', fontWeight: 600,
                color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.01em',
              }}>
                Test Case Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                ref={nameRef}
                type="text"
                value={testCase.name}
                onChange={e => { setTestCase({ ...testCase, name: e.target.value }); setErrors({ ...errors, name: null }); }}
                placeholder="e.g., Unlock and verify status"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '0.625rem 0.75rem',
                  border: `1.5px solid ${errors.name ? '#fca5a5' : '#e2e8f0'}`,
                  borderRadius: '9px',
                  fontSize: '0.875rem',
                  color: '#0f172a',
                  background: errors.name ? '#fff5f5' : 'white',
                  outline: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#818cf8'; e.target.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = errors.name ? '#fca5a5' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
              {errors.name && (
                <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  ⚠ {errors.name}
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label style={{
                display: 'block', fontSize: '0.75rem', fontWeight: 600,
                color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.01em',
              }}>
                Description
              </label>
              <textarea
                value={testCase.description}
                onChange={e => setTestCase({ ...testCase, description: e.target.value })}
                placeholder="Describe what this test validates..."
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '0.625rem 0.75rem',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '9px',
                  fontSize: '0.875rem',
                  color: '#0f172a',
                  background: 'white',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: 1.5,
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#818cf8'; e.target.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Priority */}
            <div>
              <label style={{
                display: 'block', fontSize: '0.75rem', fontWeight: 600,
                color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.01em',
              }}>
                Priority
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={testCase.priority}
                  onChange={e => setTestCase({ ...testCase, priority: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.625rem 2.25rem 0.625rem 2.25rem',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '9px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: selectedPriority.color,
                    background: selectedPriority.bg,
                    outline: 'none',
                    appearance: 'none',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#818cf8'; e.target.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {/* Colored dot on left */}
                <span style={{
                  position: 'absolute', left: '0.75rem', top: '50%',
                  transform: 'translateY(-50%)',
                  width: 8, height: 8, borderRadius: '50%',
                  background: selectedPriority.dot,
                  pointerEvents: 'none',
                }} />
                {/* Chevron on right */}
                <svg
                  width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke={selectedPriority.color} strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '0.875rem 1.5rem 1.25rem',
            display: 'flex',
            gap: '0.625rem',
            justifyContent: 'flex-end',
            borderTop: '1px solid #f1f5f9',
          }}>
            <button
              onClick={close}
              style={{
                padding: '0.5rem 1.125rem',
                border: '1.5px solid #e2e8f0',
                borderRadius: '9px',
                background: 'white',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#64748b',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '0.5rem 1.25rem',
                border: 'none',
                borderRadius: '9px',
                background: testCase.name.trim()
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : '#e2e8f0',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: testCase.name.trim() ? 'white' : '#94a3b8',
                cursor: testCase.name.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                boxShadow: testCase.name.trim() ? '0 4px 12px rgba(99,102,241,0.3)' : 'none',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (testCase.name.trim()) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(99,102,241,0.4)'; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = testCase.name.trim() ? '0 4px 12px rgba(99,102,241,0.3)' : 'none'; }}
            >
              <span>🧪</span> Create Test Case
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateNewTestCaseModal;